import type {
  TaskMetadataDisplayCtx,
  TaskReminderFormState,
  TaskReminderFormValues,
  TaskTab,
} from 'src/types/task-and-reminder';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';

import * as React from 'react';
import moment from 'moment-timezone';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'src/rerouter';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {
  addTask,
  updateTask,
  deleteTask,
  getTaskDetails,
} from 'src/action-creators/task-and-reminder';

import {selectCurrentAccount} from 'src/selectors/accounts';
import {
  selectIsTaskDetailsLoading,
  selectTaskDetails,
} from 'src/selectors/task-and-reminder';

import {
  MAX_REMINDERS,
  buildCreateTaskPayload,
  buildUpdateTaskPayload,
  createReminderSelection,
  getDefaultFormValues,
  getReminderOptions,
  mapTaskDetailsToInitialValues,
  normalizeInitialValues,
  trackTaskEditEvents,
  getFilledFieldCount,
  resolveTasksTabFromQuery,
} from 'src/utils/task-and-reminder';
import classify from 'src/utils/classify';
import {AnalyticsService} from 'src/analytics';
import {TASK_AND_REMINDER} from 'src/analytics/constants_event';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Checkbox} from '@spaced-out/ui-design-system/lib/components/Checkbox';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {
  Toast,
  ToastTitle,
  TOAST_SEMANTIC,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {
  BodySmall,
  FormLabelMedium,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';

import RichTextEditor from 'src/components/lib/rich-text-editor/rich-text-editor';
import {DeleteTaskConfirmationDialog} from 'src/components/task-and-reminder/dialogs/delete-task-confirmation-dialog';
import {EditTaskConfirmationDialog} from 'src/components/task-and-reminder/dialogs/edit-task-confirmation-dialog';
import {TaskCandidateSelectField} from 'src/components/task-and-reminder/lib/task-candidate-select-field';
import {TaskJobSelectField} from 'src/components/task-and-reminder/lib/task-job-select-field';
import {TaskTypeSelect} from 'src/components/task-and-reminder/lib/task-type-select';
import {UserSelect} from 'src/components/task-and-reminder/lib/user-select-field';
import {MetadataDetails} from 'src/components/task-and-reminder/task-details/metadata-details';

import css from './task-and-reminder-form.css';


type TaskAndReminderFormProps = {
  mode?: 'add' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  taskId?: number | string | null;
  tab?: TaskTab;
};

const TaskAndReminderForm = ({
  mode = 'add',
  isOpen,
  onClose,
  taskId: taskIdProp,
  tab: tabProp,
}: TaskAndReminderFormProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const taskDetails = useSelector(selectTaskDetails);
  const isTaskDetailsLoading = useSelector(selectIsTaskDetailsLoading);
  const {t} = useLabelTranslation();
  const userAgent = useSelector(selectCurrentAccount);
  const taskFormTimezone = moment.tz.guess();

  const initialValues: TaskReminderFormState = {
    ...getDefaultFormValues(),
    assignee_id: userAgent?.id ? String(userAgent.id) : '',
  };
  const isEditMode = mode === 'edit';

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = React.useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formValues, setFormValues] =
    React.useState<TaskReminderFormState>(initialValues);
  const [errors, setErrors] = React.useState<Record<string, boolean>>({
    title: true,
    assignee_id: false,
    due_date: false,
    due_time: false,
  });
  const panelOpenTimeRef = React.useRef(Date.now());

  const primaryButtonText = isEditMode ? t('Save', 'Save') : t('Add', 'Add');
  const pageQuery = location?.query ?? {};
  const {taskId: taskIdFromQuery = ''} = pageQuery ?? {};
  const currentTab = resolveTasksTabFromQuery(pageQuery);

  const resolvedEditTaskId = taskIdProp ?? (taskIdFromQuery || null);

  const isSystemGeneratedTask = taskDetails?.creator_type === 'system';

  const derivedDisplayConfig = React.useMemo(():
    | Array<TaskMetadataDisplayCtx>
    | undefined => {
    if (isSystemGeneratedTask) {
      return undefined;
    }
    const entries: Array<TaskMetadataDisplayCtx> = [];
    if (formValues.candidate_details?.name) {
      entries.push({
        label: 'Candidate Name',
        value: formValues.candidate_details.name,
      });
    }
    if (formValues.job_details?.title) {
      entries.push({label: 'Job Title', value: formValues.job_details.title});
    }
    return entries.length ? entries : undefined;
  }, [
    isSystemGeneratedTask,
    formValues.candidate_details,
    formValues.job_details,
  ]);

  const taskMetadataOptions = React.useMemo(
    () => ({
      existingMetadata: isEditMode ? taskDetails?.metadata ?? null : null,
      preserveJobCandidateFromExisting: isSystemGeneratedTask,
      displayConfig: derivedDisplayConfig,
    }),
    [
      isEditMode,
      isSystemGeneratedTask,
      taskDetails?.metadata,
      derivedDisplayConfig,
    ],
  );

  const showToastMessage = (
    semantic: typeof TOAST_SEMANTIC.success | typeof TOAST_SEMANTIC.danger,
    message: string,
  ) => {
    toastApi.show(
      <Toast initialFocus={0} semantic={semantic}>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
    );
  };

  const validateForm = React.useCallback(
    (values: TaskReminderFormState) => {
      const requiredFields: Array<keyof TaskReminderFormState> = [
        'assignee_id',
        'due_date',
        'due_time',
      ];
      if (!isSystemGeneratedTask) {
        requiredFields.unshift('title');
      }
      const nextErrors: Record<string, boolean> = {};
      requiredFields.forEach((field) => {
        const raw = values[field];
        const asString = typeof raw === 'string' ? raw : '';
        if (!asString || asString.trim() === '') {
          nextErrors[field] = true;
        }
      });
      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    },
    [isSystemGeneratedTask],
  );

  React.useEffect(() => {
    if (isOpen) {
      panelOpenTimeRef.current = Date.now();
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isEditMode || !resolvedEditTaskId) {
      return;
    }
    dispatch(getTaskDetails(resolvedEditTaskId!));
  }, [dispatch, isEditMode, resolvedEditTaskId]);

  React.useEffect(() => {
    if (isTaskDetailsLoading || !taskDetails) {
      return;
    }
    const nextInitialValues = normalizeInitialValues(
      mapTaskDetailsToInitialValues(taskDetails, taskFormTimezone),
    );
    setFormValues(nextInitialValues);
    validateForm(nextInitialValues);
  }, [isTaskDetailsLoading, taskDetails, taskFormTimezone, validateForm]);

  const handleSubmit = async () => {
    if (!validateForm(formValues)) {
      return;
    }

    const submitValues: TaskReminderFormValues = {
      ...formValues,
      reminders: formValues.reminders
        .map((reminder) => reminder.option)
        .filter(Boolean),
    };

    try {
      if (!isEditMode) {
        setIsSubmitting(true);
        await dispatch(
          addTask(
            buildCreateTaskPayload(
              submitValues,
              taskMetadataOptions,
              taskFormTimezone,
            ),
            pageQuery,
          ),
        );
        AnalyticsService.track(TASK_AND_REMINDER.TASK_CREATED, {
          task_name: submitValues.title,
          creator_id: userAgent?.id,
          creator_name: userAgent?.name,
          assignee_id: submitValues.assignee_id,
          assignee_type:
            submitValues.assignee_id === String(userAgent?.id)
              ? 'self'
              : 'other',
          due_date: submitValues.due_date,
          has_cc: submitValues.cc.length > 0 ? 'Y' : 'N',
          cc_count: submitValues.cc.length,
          reminder_count: submitValues.reminders.length,
        });
        showToastMessage(
          TOAST_SEMANTIC.success,
          t('Task_created_successfully', 'Task created successfully'),
        );
        setIsSubmitting(false);
        onClose();
      } else if (!isUpdateDialogOpen) {
        setIsUpdateDialogOpen(true);
      } else {
        setIsSubmitting(true);
        await dispatch(
          updateTask(
            buildUpdateTaskPayload(
              submitValues,
              taskMetadataOptions,
              taskFormTimezone,
            ),
            resolvedEditTaskId!,
            tabProp ?? currentTab.tabId,
          ),
        );

        trackTaskEditEvents(resolvedEditTaskId, taskDetails, submitValues);

        showToastMessage(
          TOAST_SEMANTIC.success,
          t('Task_updated_successfully', 'Task updated successfully'),
        );
        setIsSubmitting(false);
        onClose();
      }
    } catch {
      setIsSubmitting(false);
      showToastMessage(
        TOAST_SEMANTIC.danger,
        isEditMode
          ? t('Failed_to_update_task', 'Failed to update task')
          : t('Failed_to_create_task', 'Failed to create task'),
      );
    }
  };

  const handleFieldChange = (
    field: keyof TaskReminderFormState,
    value: string | Array<string>,
  ) => {
    validateForm({...formValues, [field]: value});
    setFormValues((previousState) => ({
      ...previousState,
      [field]: value,
    }));
  };

  const handleJobDetailsChange = (
    selectedJob: TaskReminderFormState['job_details'],
  ) => {
    const nextValues = {...formValues, job_details: selectedJob};
    validateForm(nextValues);
    setFormValues((previousState) => ({
      ...previousState,
      job_details: selectedJob,
    }));
  };

  const handleCandidateDetailsChange = (
    selectedCandidate: TaskReminderFormState['candidate_details'],
  ) => {
    const nextValues = {...formValues, candidate_details: selectedCandidate};
    validateForm(nextValues);
    setFormValues((previousState) => ({
      ...previousState,
      candidate_details: selectedCandidate,
    }));
  };

  const handleReminderChange = (value: string, reminderId: string) => {
    setFormValues((previousState) => ({
      ...previousState,
      reminders: previousState.reminders.map((item) =>
        item.id === reminderId ? {...item, option: value} : item,
      ),
    }));
  };

  const handleAddReminder = () => {
    if (formValues.reminders.length >= MAX_REMINDERS) {
      return;
    }
    setFormValues((previousState) => ({
      ...previousState,
      reminders: [...previousState.reminders, createReminderSelection('')],
    }));
  };

  const handleRemoveReminder = (reminderId: string) => {
    setFormValues((previousState) => ({
      ...previousState,
      reminders: previousState.reminders.filter(
        (reminder) => reminder.id !== reminderId,
      ),
    }));
  };

  const handleClose = () => {
    if (!isEditMode) {
      const filledFieldCount = getFilledFieldCount(formValues);
      if (filledFieldCount > 0) {
        AnalyticsService.track(TASK_AND_REMINDER.TASK_CREATION_ABANDONED, {
          source_context: 'task_form',
          fields_filled: filledFieldCount,
          time_spent_ms: Date.now() - panelOpenTimeRef.current,
        });
      }
    }
    onClose();
  };

  const handleDeleteTask = async () => {
    if (!resolvedEditTaskId || isDeleteSubmitting) {
      return;
    }

    setIsDeleteSubmitting(true);
    try {
      await dispatch(deleteTask(resolvedEditTaskId));
      AnalyticsService.track(TASK_AND_REMINDER.TASK_DELETED, {
        task_id: resolvedEditTaskId,
        task_type: taskDetails?.task_type,
        task_status_at_deletion: taskDetails?.status,
        creator_id: taskDetails?.created_by,
        is_own_task: String(taskDetails?.created_by) === String(userAgent?.id),
      });
      setIsDeleteDialogOpen(false);
      showToastMessage(
        TOAST_SEMANTIC.success,
        t('Task_deleted_successfully', 'Task deleted successfully'),
      );
      onClose();
    } catch {
      showToastMessage(
        TOAST_SEMANTIC.danger,
        t('Failed_to_delete_task', 'Failed to delete task'),
      );
    } finally {
      setIsDeleteSubmitting(false);
    }
  };

  return (
    <>
      <Panel
        isOpen={isOpen || !!taskIdFromQuery}
        onClose={handleClose}
        anchor="right"
        size="medium"
        hideBackdrop={false}
      >
        <PanelHeader onCloseButtonClick={handleClose} size="medium">
          {!isEditMode
            ? t('Add_Task', 'Add Task')
            : taskDetails?.title || t('Edit_Task', 'Edit Task')}
        </PanelHeader>
        {isTaskDetailsLoading ? (
          <CircularLoader className={css.loader} />
        ) : (
          <>
            <PanelBody className={css.formBody}>
              {isEditMode && (
                <div className={css.markAsCompletedCheckbox}>
                  <Checkbox
                    checked={formValues.status === 'completed'}
                    onChange={(checked: {value: string; checked: boolean}) =>
                      handleFieldChange(
                        'status',
                        checked.checked ? 'completed' : 'created',
                      )
                    }
                    disabled={isSystemGeneratedTask}
                  />
                  <FormLabelSmall
                    color={isSystemGeneratedTask ? 'disabled' : 'secondary'}
                  >
                    {t('Mark_as_completed', 'Mark as completed')}
                  </FormLabelSmall>
                </div>
              )}
              {isEditMode && taskDetails?.metadata ? (
                <MetadataDetails
                  metadata={
                    isSystemGeneratedTask || !derivedDisplayConfig?.length
                      ? taskDetails.metadata
                      : {
                          ...taskDetails.metadata,
                          display_config: derivedDisplayConfig,
                        }
                  }
                />
              ) : !isEditMode && derivedDisplayConfig?.length ? (
                <MetadataDetails
                  metadata={{
                    cta_config: [],
                    display_config: derivedDisplayConfig,
                  }}
                />
              ) : null}
              {!isSystemGeneratedTask ? (
                <Input
                  label={t('Task_Name', 'Task Name')}
                  placeholder={t('Enter_the_Task_Name', 'Enter the Task Name')}
                  value={formValues.title}
                  onChange={(event) =>
                    handleFieldChange('title', event.target.value)
                  }
                  required
                  error={errors.title}
                  errorText={t(
                    'Task_Name_is_required',
                    'Task Name is required',
                  )}
                />
              ) : null}
              <RichTextEditor
                label={t('Description', 'Description')}
                placeholder={t('Description', 'Description')}
                value={formValues.description}
                onChange={(value) =>
                  handleFieldChange('description', value || '')
                }
                showActionsButtons={[
                  'bold',
                  'italic',
                  'underline',
                  'alignLeft',
                  'alignCenter',
                  'alignRight',
                  'unorderedList',
                  'orderedList',
                  'link',
                ]}
              />
              <TaskTypeSelect
                label={t('Task_Category', 'Task Category')}
                placeholder={t('Select', 'Select')}
                value={formValues.task_type}
                onChange={(taskTypeKey) =>
                  handleFieldChange('task_type', taskTypeKey)
                }
                disabled={isEditMode}
                isActive={isOpen}
              />
              {!isSystemGeneratedTask ? (
                <>
                  <TaskJobSelectField
                    className={css.userSelectField}
                    label={t('Add_Job', 'Add Job')}
                    placeholder={t(
                      'Search_and_Select_Job',
                      'Search and Select Job',
                    )}
                    value={formValues.job_details}
                    onChange={handleJobDetailsChange}
                  />
                  <TaskCandidateSelectField
                    className={css.userSelectField}
                    label={t('Tag_Candidate', 'Tag Candidate')}
                    placeholder={t(
                      'Search_and_Select_Candidate',
                      'Search and Select Candidate',
                    )}
                    value={formValues.candidate_details}
                    onChange={handleCandidateDetailsChange}
                  />
                </>
              ) : null}
              <UserSelect
                className={css.userSelectField}
                label={t('Assignee', 'Assignee')}
                required
                selectedKeys={
                  formValues.assignee_id ? [formValues.assignee_id] : []
                }
                placeholder={t(
                  'Search_and_Select_Assignee',
                  'Search and Select Assignee',
                )}
                limit={1}
                onSelectionChange={(selectedKeys: Array<string>) => {
                  handleFieldChange('assignee_id', selectedKeys[0] || '');
                }}
                isError={errors.assignee_id}
                errorText={t('Assignee_is_required', 'Assignee is required')}
              />
              <UserSelect
                className={css.userSelectField}
                label={t('Add_CC_to', 'Add CC to')}
                selectedKeys={formValues.cc}
                placeholder={t(
                  'Search_and_Select_CC_recipients',
                  'Search and Select CC recipients',
                )}
                limit={50}
                onSelectionChange={(selectedKeys: Array<string>) => {
                  handleFieldChange('cc', selectedKeys);
                }}
              />
              <div className={css.dateTimeSection}>
                <div className={css.dateTimeHeader}>
                  <FormLabelMedium>
                    {t('Reminder_Add_Due_Date', 'Reminder & Add Due Date')}
                  </FormLabelMedium>
                  <BodySmall color="secondary">
                    {t(
                      'This_will_help_you_remind_via_email_and_in_the_notification',
                      'This will help you remind via email and in the notification',
                    )}
                  </BodySmall>
                </div>
                <div className={css.dateTimeFieldsRow}>
                  <Input
                    label={t('Due_Date', 'Due Date')}
                    type="date"
                    value={formValues.due_date}
                    onChange={(event) =>
                      handleFieldChange('due_date', event.target.value)
                    }
                    required
                    error={errors.due_date}
                    errorText={t(
                      'Due_date_is_required',
                      'Due date is required',
                    )}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <Input
                    label={t('Due_Time', 'Due Time')}
                    type="time"
                    value={formValues.due_time}
                    onChange={(event) =>
                      handleFieldChange('due_time', event.target.value)
                    }
                    required
                    error={errors.due_time}
                    errorText={t(
                      'Due_time_is_required',
                      'Due time is required',
                    )}
                  />
                </div>
                <div className={css.reminderSection}>
                  {formValues.reminders.map((reminder) => (
                    <div className={css.reminderRow} key={reminder.id}>
                      <SimpleDropdown
                        options={getReminderOptions(t)}
                        selectedKeys={reminder.option ? [reminder.option] : []}
                        onChange={(option: MenuOption) => {
                          handleReminderChange(option.key, reminder.id);
                        }}
                        placeholder={t('Select', 'Select')}
                      />
                      <Button
                        type="ghost"
                        onClick={() => handleRemoveReminder(reminder.id)}
                        ariaLabel={t('Remove_reminder', 'Remove reminder')}
                        iconLeftName="trash"
                      />
                    </div>
                  ))}
                  <Link
                    onClick={handleAddReminder}
                    iconLeftName="add"
                    disabled={formValues.reminders.length >= MAX_REMINDERS}
                  >
                    {formValues.reminders.length > 0
                      ? t('Add_more_reminders', 'Add more reminders')
                      : t('Add_reminder', 'Add reminder')}
                  </Link>
                </div>
              </div>
            </PanelBody>
            <PanelFooter
              classNames={{
                wrapper: classify({[css.footerActions]: isEditMode}),
                actions: classify({[css.footerActions]: isEditMode}),
              }}
            >
              {isEditMode ? (
                !isSystemGeneratedTask ? (
                  <Button
                    type="danger"
                    iconLeftName="trash"
                    onClick={() => setIsDeleteDialogOpen(true)}
                    ariaLabel={t('Delete_Task', 'Delete Task')}
                    testId="task-and-reminder-form-delete"
                  />
                ) : (
                  <Tooltip
                    body={t(
                      'You_cannot_delete_a_system_generated_task',
                      'You cannot delete a system generated task',
                    )}
                    elevation="backdrop"
                  >
                    <div>
                      <Button
                        type="danger"
                        iconLeftName="trash"
                        disabled
                        ariaLabel={t('Delete_Task', 'Delete Task')}
                        testId="task-and-reminder-form-delete"
                      />
                    </div>
                  </Tooltip>
                )
              ) : null}
              <div className={css.footerRightActions}>
                <Button
                  type="ghost"
                  onClick={handleClose}
                  ariaLabel={t('Cancel', 'Cancel')}
                  testId="task-and-reminder-form-cancel"
                >
                  {t('Cancel', 'Cancel')}
                </Button>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  ariaLabel={primaryButtonText}
                  disabled={Object.values(errors).some((error) => error)}
                  testId="task-and-reminder-form-submit"
                >
                  {primaryButtonText}
                </Button>
              </div>
            </PanelFooter>
          </>
        )}
      </Panel>
      {isDeleteDialogOpen ? (
        <DeleteTaskConfirmationDialog
          isOpen={isDeleteDialogOpen}
          isDeleting={isDeleteSubmitting}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteTask}
        />
      ) : null}
      {isUpdateDialogOpen ? (
        <EditTaskConfirmationDialog
          isOpen={isUpdateDialogOpen}
          isUpdating={isSubmitting}
          onClose={() => setIsUpdateDialogOpen(false)}
          onConfirm={handleSubmit}
        />
      ) : null}
    </>
  );
};

export default TaskAndReminderForm;
